import React, {ReactElement} from "react";
import {ICellProps, IColumnExtended, IReportData} from "./types";
import IconButton from "@mui/material/IconButton";
import {Tooltip} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Columns from "./columns";
import * as excelJS from "exceljs"


interface IProps {
  reportData?: IReportData[] | null;
}


const headerFill: any = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FF00ACC1" } // ARGB: FF = opaque, 4472C4 = blue
};

const Toolbar = ({ reportData }: IProps): ReactElement => {


  const onExportData = async (): Promise<void> => {
    if (!reportData || reportData.length === 0) return;

    try {
       const workbook = new excelJS.Workbook();
       const sheet = workbook.addWorksheet("ms-rm-report");

      // Build headers

      let prevParentLabel = "";

      let flagA = 0;
      let flagB = 0;
      const headerA: any[] = [];
      const headerB: any[] = [];
      const headerC: any[] = [];
      const mergedCellsA: { start: number; end: number }[] = [];
      const mergedCellsB: { start: number; end: number }[] = [];


      Columns.forEach((col, cIndex) => {

        if (flagA === 0) {
          if (col.labelA) {
            headerA.push(col.labelA.label);

            if (col.labelA.span > 1) {
              for (let i = 1; i < col.labelA.span; i++) {
                headerA.push(null);
              }

              mergedCellsA.push({ start: cIndex + 1, end: cIndex + col.labelA.span });
            }

            flagA = (col.labelA.span ?? 1);
          } else {
            headerA.push("");
          }
        }

        if (flagB === 0) {
          if (col.labelB) {
            headerB.push(col.labelB.label);

            if (col.labelB.span > 1) {
              for (let i = 1; i < col.labelB.span; i++) {
                headerB.push(null);
              }

              mergedCellsB.push({ start: cIndex + 1, end: cIndex + col.labelB.span });
            }

            flagB = (col.labelB.span ?? 1);
          } else {
            headerB.push("");
          }
        }

        flagA = flagA > 0 ? flagA - 1 : 0;
        flagB = flagB > 0 ? flagB - 1 : 0;

        headerC.push(col.subLabel ? `${col.label} ${col.subLabel}` : col.label);
      });

      sheet.addRow(headerB);
      sheet.addRow(headerA);
      sheet.addRow(headerC);

      mergedCellsA.forEach(m => {
        sheet.mergeCells(2, m.start, 2, m.end);
      });

      mergedCellsB.forEach(m => {
        sheet.mergeCells(1, m.start, 1, m.end);
      });

      // Style header row
      sheet.getRow(1).eachCell((cell) => {
        cell.fill = headerFill;
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; // white text
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
      sheet.getRow(2).eachCell((cell) => {
        cell.fill = headerFill;
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; // white text
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
      sheet.getRow(3).eachCell((cell) => {
        cell.fill = headerFill;
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; // white text
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });

      // Add data rows
      for (const row of reportData) {
        const millOrders = Object.values(row.mill);
        
        for (const millOrder of millOrders) {
          for (let strandIndex = 0; strandIndex < millOrder.strands.length; strandIndex++) {
            const rowValues: any[] = [];

            for (const column of Columns) {
              const value = column.accessor
                  ? column.accessor(row.caster, millOrder, strandIndex, true)
                  : (row as any)[column.id];

              rowValues.push(value);
            }

            sheet.addRow(rowValues);
          }
        }
      }

      // Auto width for columns (simple heuristic)
      sheet.columns.forEach((column: any) => {
        let maxLength = 10;

        column.eachCell({ includeEmpty: true }, (cell: any) => {
          const val = cell.value;
          const str = val == null ? "" : (val instanceof Date ? val.toISOString() : String(val));
          if (str.length > maxLength) maxLength = str.length;
        });

        column.width = Math.min(Math.max(maxLength + 2, 10), 50);
      });

      // Apply date format to cells that are dates
      // sheet.eachRow((row) => {
      //   row.eachCell((cell) => {
      //     if (cell.value instanceof Date) {
      //       cell.numFmt = "yyyy-mm-dd hh:mm:ss";
      //     }
      //   });
      // });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const now = new Date();
      const filename = `report_${now.toISOString().slice(0,19).replace(/[:T]/g, "-")}.xlsx`;

      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed", err);
    }
  };

  return (
      <div className="display-flex-row gap-1">
        <IconButton color="primary" onClick={onExportData} disabled={!reportData || reportData.length === 0} >
          <Tooltip title={"Export data"}>
            <FileDownloadIcon/>
          </Tooltip>
        </IconButton>
      </div>
  );
}

export default Toolbar;